import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import websocketService from '../services/websocket';
import logger from '../services/logger';
import { usePermissions } from './PermissionContext';

// Axios base URL'ini ayarla - Always use HTTPS [v2 - force new bundle hash]
logger.debug('🔍 AuthContext - REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
logger.debug('🔍 AuthContext - REACT_APP_WS_URL:', process.env.REACT_APP_WS_URL);
logger.debug('🔍 AuthContext - axios.defaults BEFORE:', axios.defaults.baseURL);

// Always use absolute HTTPS URL from env
if (process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL.trim()) {
  axios.defaults.baseURL = process.env.REACT_APP_API_URL;
  logger.info('✅ AuthContext - Using env REACT_APP_API_URL:', axios.defaults.baseURL);
} else {
  // Fallback to HTTPS origin
  if (typeof window !== 'undefined') {
    axios.defaults.baseURL = `https://${window.location.host}`;
    logger.warn('⚠️ AuthContext - Fallback to window origin:', axios.defaults.baseURL);
  }
}
logger.debug('🔍 AuthContext - Final baseURL:', axios.defaults.baseURL);

// Helper function to get CSRF token from cookie
const getCsrfToken = () => {
  const match = document.cookie.match(/csrf_token=([^;]+)/);
  return match ? match[1] : null;
};

// CRITICAL: Request interceptor to FORCE all requests to use HTTPS + CSRF protection
axios.interceptors.request.use(
  config => {
    // Ensure baseURL is always HTTPS
    if (config.baseURL && config.baseURL.startsWith('http:')) {
      config.baseURL = config.baseURL.replace('http://', 'https://');
      logger.warn('⚠️ INTERCEPTOR - Forcing HTTP to HTTPS:', config.baseURL);
    }

    // If URL is absolute and HTTP, force HTTPS
    if (config.url && config.url.startsWith('http://')) {
      config.url = config.url.replace('http://', 'https://');
      logger.warn('⚠️ INTERCEPTOR - Forcing URL HTTP to HTTPS:', config.url);
    }

    // CSRF Protection: Add CSRF token for state-changing methods
    // Backend CSRF middleware requires X-CSRF-Token header for POST/PUT/DELETE/PATCH
    const method = config.method?.toUpperCase();
    const unsafeMethods = ['POST', 'PUT', 'DELETE', 'PATCH'];

    if (unsafeMethods.includes(method)) {
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }

    logger.debug('🔍 INTERCEPTOR - Final request URL:', config.baseURL, config.url);
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);
// Sayfa yenilendiğinde mevcut token'ı tekrar Authorization header'ına yükle
const persistedToken = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
if (persistedToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${persistedToken}`;
}

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const { loadPermissions, clearPermissions } = usePermissions();

  // Load frontend log level from system settings
  const loadFrontendLogLevel = async () => {
    try {
      const response = await axios.get('/api/v1/system-settings');
      if (response.data.frontend_log_level !== undefined) {
        logger.setLogLevel(response.data.frontend_log_level);
        logger.debug('Frontend log level loaded from database:', response.data.frontend_log_level);
      }
    } catch (error) {
      // Silently fail - use default log level
      logger.debug('Could not load frontend log level from database, using default');
    }
  };

  // Axios interceptor for authentication
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get('/api/v1/auth/me');
          setUser(response.data);

          // Load frontend log level from database
          await loadFrontendLogLevel();

          // Load permissions from API
          await loadPermissions();

          // CRITICAL FIX: Connect WebSocket on page reload
          // WebSocket bağlantısını kur (sayfa yenilendiğinde de)
          websocketService.connect(token);
        } catch (error) {
          logger.error('Authentication check failed:', error);
          // Logout logic inline to avoid circular dependency
          setUser(null);
          setToken(null);
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
          clearPermissions();
          websocketService.disconnect();
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const login = async (username, password) => {
    try {
      // OAuth2PasswordRequestForm ile uyumlu form-encoded gönderim
      const form = new URLSearchParams();
      form.append('username', username);
      form.append('password', password);

      const response = await axios.post('/api/v1/auth/login', form, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });

      const { access_token } = response.data;

      // Token'ı localStorage'a kaydet
      localStorage.setItem('token', access_token);

      // Axios header'ını manuel olarak ayarla
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      // State'i güncelle
      setToken(access_token);

      // Get user info - token ayarlandıktan sonra
      const userResponse = await axios.get('/api/v1/auth/me');
      setUser(userResponse.data);

      // Load frontend log level from database
      await loadFrontendLogLevel();

      // Load permissions from API
      await loadPermissions();

      // WebSocket bağlantısını kur - token kontrolü ile
      if (access_token) {
        websocketService.connect(access_token);
      }

      return { success: true };
    } catch (error) {
      logger.error('Login failed:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Giriş başarısız',
      };
    }
  };

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];

    // Clear permissions
    clearPermissions();

    // WebSocket bağlantısını kapat
    websocketService.disconnect();
  }, [clearPermissions]);

  const register = async (userData) => {
    try {
      const response = await axios.post('/api/v1/auth/register', userData);
      return { success: true, data: response.data };
    } catch (error) {
      logger.error('Registration failed:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Kayıt başarısız',
      };
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    register,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
